import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import Link from 'next/link';
import { FaEllipsisV } from 'react-icons/fa';

export interface ContextMenuItem {
  label: string;
  onClick?: () => void;
  to?: string;
  leftAccessory?: React.ReactNode;
}

interface ContextMenuProps {
  className?: string;
  items: Array<ContextMenuItem>;
  size?: 'sm' | 'md' | 'lg';
}

export const ContextMenu = ({
  className,
  items,
  size = 'md',
}: ContextMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div ref={menuRef} className={clsx(className ? className : 'relative')}>
      <button
        className={clsx(
          'inline-flex items-center justify-center',
          'rounded-md text-neutral-500',
          size === 'sm' && 'text-sm w-7 h-7',
          size === 'md' && 'text-base w-10 h-10',
          size === 'lg' && 'text-lg w-12 h-12',
          isOpen && 'bg-green-light',
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        <FaEllipsisV />
      </button>

      <div
        className={clsx(
          'absolute left-full top-0',
          isOpen ? 'block' : 'hidden',
        )}
      >
        <div className="relative bg-white shadow-lg z-50">
          <ul>
            {items.map((item, index) => (
              <li key={index}>
                {item.to ? (
                  <Link
                    href={item.to}
                    className="leading-10 px-3 hover:bg-green-light text-nowrap"
                  >
                    {item.leftAccessory}
                    {item.label}
                  </Link>
                ) : (
                  <button
                    onClick={item.onClick}
                    className="leading-10 px-3 hover:bg-green-light text-nowrap"
                  >
                    {item.leftAccessory}
                    {item.label}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
